/**
 * LoadSEER API Provider Component
 * Authentication and connection management for LoadSEER API integration
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import { LoadSEERApiClient } from '../api';
import { validateApiBaseUrl, validateApiToken } from '../utils';
import type { NodeTypeDto } from '../types';
import { env } from '../../../env.config';

// ============================================================================
// Styled Components
// ============================================================================

const ProviderCard = styled.div`
  padding: 1.25rem 1.5rem;
  background: ${props => props.theme?.panelBackground || '#0d1117'};
  color: ${props => props.theme?.textColor || '#e6edf3'};
  border-radius: 8px;
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${props => props.theme?.textColor || '#e6edf3'};
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme?.textColor || '#e6edf3'};
`;

const Input = styled.input`
  padding: 0.5rem;
  background: ${props => props.theme?.inputBgd || '#161b22'};
  color: ${props => props.theme?.textColor || '#e6edf3'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.primaryColor || '#58a6ff'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin: 0;
`;

const Button = styled.button.withConfig({
  shouldForwardProp: prop => (prop as string) !== 'variant',
})<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  ${props => {
    if (props.variant === 'danger') {
      return `
        background: #da3633;
        color: #ffffff;
        &:hover:not(:disabled) {
          background: #c92a2a;
        }
      `;
    }
    if (props.variant === 'secondary') {
      return `
        background: ${props.theme?.secondaryInputBgd || '#21262d'};
        color: ${props.theme?.textColor || '#e6edf3'};
        border: 1px solid ${props.theme?.borderColor || '#30363d'};
        &:hover:not(:disabled) {
          background: ${props.theme?.secondaryInputBgdHover || '#30363d'};
        }
      `;
    }
    // primary
    return `
      background: ${props.theme?.buttonBgd || '#238636'};
      color: #ffffff;
      &:hover:not(:disabled) {
        background: ${props.theme?.buttonBgdHover || '#2ea043'};
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div.withConfig({
  shouldForwardProp: prop => (prop as string) !== 'type',
})<{ type: 'success' | 'error' | 'info' }>`
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;

  ${props => {
    if (props.type === 'error') {
      return `
        background: rgba(248, 81, 73, 0.1);
        color: #ff7b72;
        border: 1px solid rgba(248, 81, 73, 0.3);
      `;
    }
    if (props.type === 'success') {
      return `
        background: rgba(46, 160, 67, 0.1);
        color: #3fb950;
        border: 1px solid rgba(46, 160, 67, 0.3);
      `;
    }
    return `
      background: rgba(88, 166, 255, 0.1);
      color: #58a6ff;
      border: 1px solid rgba(88, 166, 255, 0.3);
    `;
  }}
`;

// ============================================================================
// Icon Component
// ============================================================================

export const LoadSEERIcon: FC = () => (
  <img
    src='/assets/loadseer-icon.png'
    alt='LoadSEER API'
    style={{
      width: 48,
      height: 48,
      borderRadius: 8,
    }}
  />
);

// ============================================================================
// Props & State Types
// ============================================================================

export interface LoadSEERProviderProps {
  onAuthenticated?: (client: LoadSEERApiClient, nodeTypes: NodeTypeDto[]) => void;
  onDisconnected?: () => void;
}

type AuthState = 'idle' | 'validating' | 'authenticated' | 'error';

// ============================================================================
// Main Component
// ============================================================================

export const LoadSEERProvider: FC<LoadSEERProviderProps> = ({
  onAuthenticated,
  onDisconnected,
}) => {
  // Form state - use proxy in dev mode, otherwise use env variable
  // NOTE: In dev mode, we default to '/loadseer-proxy' (configured in vite-dev.ts)
  //       to avoid CORS issues during local development. This can be overridden
  //       by the user via the form input. In production, uses VITE_LOADSEER_API_BASE_URL.
  const defaultBaseUrl = import.meta.env.DEV
    ? '/loadseer-proxy'
    : (env.VITE_LOADSEER_API_BASE_URL || '').trim();
  const defaultApiToken = (env.VITE_LOADSEER_API_TOKEN || '').trim();
  const [apiBaseUrl, setApiBaseUrl] = useState(defaultBaseUrl);
  const [apiToken, setApiToken] = useState(defaultApiToken);
  const [showToken, setShowToken] = useState(false);

  // Connection state
  const [authState, setAuthState] = useState<AuthState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [client, setClient] = useState<LoadSEERApiClient | null>(null);

  // Validation
  const urlValidation = useMemo(() => validateApiBaseUrl(apiBaseUrl), [apiBaseUrl]);
  const tokenValidation = useMemo(() => validateApiToken(apiToken), [apiToken]);
  const canConnect = urlValidation.valid && tokenValidation.valid && authState !== 'validating';

  // Connect handler
  const handleConnect = useCallback(async () => {
    if (!canConnect) return;

    setAuthState('validating');
    setErrorMsg(null);

    try {
      const newClient = new LoadSEERApiClient({
        baseUrl: apiBaseUrl,
        apiToken: apiToken,
      });

      // Test connection and fetch node types
      await newClient.testConnection();
      const nodeTypesResponse = await newClient.getNodeTypes();

      setClient(newClient);
      setAuthState('authenticated');

      onAuthenticated?.(newClient, nodeTypesResponse.results);
    } catch (error) {
      setAuthState('error');
      setErrorMsg(error instanceof Error ? error.message : 'Failed to connect to LoadSEER API');
    }
  }, [apiBaseUrl, apiToken, canConnect, onAuthenticated]);

  // Disconnect handler
  const handleDisconnect = useCallback(() => {
    setClient(null);
    setAuthState('idle');
    setErrorMsg(null);
    setApiToken(''); // Clear token for security

    onDisconnected?.();
  }, [onDisconnected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setApiToken(''); // Clear sensitive data
    };
  }, []);

  return (
    <ProviderCard data-testid='loadseer-provider-card'>
      {authState === 'authenticated' && client ? (
        <>
          <ButtonRow>
            <Button
              variant='danger'
              onClick={handleDisconnect}
              data-testid='loadseer-disconnect-button'
            >
              Disconnect
            </Button>
          </ButtonRow>
        </>
      ) : (
        <>
          <Header>
            <LoadSEERIcon />
            <Title>LoadSEER API</Title>
          </Header>

          <FormRow>
            <Label htmlFor='loadseer-api-url'>API Base URL</Label>
            <Input
              id='loadseer-api-url'
              data-testid='loadseer-api-url-input'
              type='text'
              value={apiBaseUrl}
              onChange={e => setApiBaseUrl(e.target.value)}
              placeholder='https://ia-dev.loadseer.com/api'
              disabled={authState === 'validating'}
              aria-invalid={!urlValidation.valid && apiBaseUrl.length > 0}
              aria-describedby={!urlValidation.valid ? 'url-error' : undefined}
            />
            {!urlValidation.valid && apiBaseUrl.length > 0 && (
              <span id='url-error' style={{ fontSize: '0.75rem', color: '#ff7b72' }}>
                {urlValidation.error}
              </span>
            )}
          </FormRow>

          <FormRow>
            <Label htmlFor='loadseer-api-token'>
              API Token
              <Button
                variant='secondary'
                onClick={() => setShowToken(!showToken)}
                style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                type='button'
              >
                {showToken ? 'Hide' : 'Show'}
              </Button>
            </Label>
            <Input
              id='loadseer-api-token'
              data-testid='loadseer-api-token-input'
              type={showToken ? 'text' : 'password'}
              value={apiToken}
              onChange={e => setApiToken(e.target.value)}
              placeholder='Enter your API token'
              disabled={authState === 'validating'}
              aria-invalid={!tokenValidation.valid && apiToken.length > 0}
              aria-describedby={!tokenValidation.valid ? 'token-error' : undefined}
            />
            {!tokenValidation.valid && apiToken.length > 0 && (
              <span id='token-error' style={{ fontSize: '0.75rem', color: '#ff7b72' }}>
                {tokenValidation.error}
              </span>
            )}
          </FormRow>

          {errorMsg && <StatusMessage type='error'>{errorMsg}</StatusMessage>}

          <ButtonRow>
            <Button
              variant='primary'
              onClick={handleConnect}
              disabled={!canConnect}
              data-testid='loadseer-connect-button'
            >
              {authState === 'validating' ? 'Connecting...' : 'Connect'}
            </Button>
          </ButtonRow>

          <StatusMessage type='info'>
            <strong>Note:</strong> API tokens are session-only and never stored locally.
          </StatusMessage>
        </>
      )}
    </ProviderCard>
  );
};
